import { useState, type ReactNode } from 'react';
import {
  Activity, BellRing, BookOpenCheck, CalendarCheck, Bot, Briefcase, ClipboardCheck, FileScan, GraduationCap,
  ArrowRight, Languages, Layers, Mail, PenLine, PlayCircle, School, Smartphone, Sparkles, Users, WifiOff, Youtube,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PublicLayout, { Eyebrow, PlayButton, usePublicMeta } from './PublicLayout';
import YouTubeVideo from './YouTubeVideo';
import BookDemo from './BookDemo';
import { AD_VIDEO, CHANNEL_URL, SCHOOL_TUTORIAL_LIST, SITE, TEAM, mailto, totalMinutes } from './site';
import panelDashboard from '../../images/landing/panel-dashboard.webp';
import panelS360 from '../../images/landing/panel-s360.webp';
import panelGenerate from '../../images/landing/panel-generate.webp';
import studentToday from '../../images/landing/student-today.webp';
import studentProgress from '../../images/landing/student-progress.webp';
import studentTutor from '../../images/landing/student-tutor.webp';
import staffHome from '../../images/landing/staff-home.webp';
import staffS360 from '../../images/landing/staff-student360.webp';
import adPoster from '../../images/landing/ad-poster.webp';

const TITLE = 'YADA · AI exam prep and early warning for Ethiopian schools';
const DESCRIPTION =
  'YADA is an AI study app for Ethiopian students and an early-warning panel for their schools. ' +
  'Practise offline in English, Amharic or Afaan Oromoo; see which students need help this week.';

function Phone({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  return (
    <div className={`rounded-[34px] bg-phone-frame p-[7px] shadow-[0_30px_60px_-30px_rgba(10,25,35,.65)] ${className}`}>
      <img src={src} alt={alt} loading="lazy" className="block w-full rounded-[28px]" />
    </div>
  );
}

function Browser({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-[14px] border border-black/10 bg-surface shadow-[0_40px_80px_-40px_rgba(10,25,35,.7)] ${className}`}>
      <div className="flex h-8 items-center gap-1.5 border-b border-line bg-surface-2 px-3" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
        <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
        <span className="h-2.5 w-2.5 rounded-full bg-line-strong" />
        <span className="ml-3 truncate rounded-[6px] bg-surface px-3 py-0.5 font-mono text-[10.5px] text-muted">{SITE.domain}</span>
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </div>
  );
}

function Feature({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="rounded-[16px] border border-line bg-surface p-5 shadow-card">
      <div className="grid h-10 w-10 place-items-center rounded-[12px] bg-accent-bg text-accent-ink">{icon}</div>
      <h3 className="mt-4 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">{title}</h3>
      <p className="mt-1.5 text-[14.5px] leading-relaxed text-muted">{children}</p>
    </div>
  );
}

const SHOWCASE = [
  { key: 'dashboard', label: 'Dashboard', src: panelDashboard,
    caption: 'Scores, pass rate and who needs attention today, for the whole school or one grade.' },
  { key: 's360', label: 'Student 360', src: panelS360,
    caption: 'One page per student: a risk score explained in points, this week’s summary, study plan, cases and wellbeing.' },
  { key: 'generate', label: 'Exam generation', src: panelGenerate,
    caption: 'Draft a full exam from your own chapters with AI. Every question is reviewed before students see it.' },
] as const;

const STATS = [
  { v: '22K+', l: 'installs on Google Play' },
  { v: '4.6★', l: 'average rating' },
  { v: '3', l: 'languages: English, Amharic, Afaan Oromoo' },
  { v: 'Offline', l: 'practice on low-cost phones' },
];

// One tutorial from each stage of the series.
const FEATURED_TUTORIALS = [1, 5, 7, 13].map((n) => SCHOOL_TUTORIAL_LIST[n - 1]);

const initials = (name: string) =>
  name.replace(/^Dr\.\s*/, '').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

export default function Landing() {
  usePublicMeta(TITLE, DESCRIPTION);
  const [shot, setShot] = useState<(typeof SHOWCASE)[number]['key']>('dashboard');
  const active = SHOWCASE.find((s) => s.key === shot)!;

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-white">
        <div aria-hidden className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-slate/30 blur-3xl" />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-12 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:pb-24 lg:pt-20">
          <div>
            <Eyebrow onNavy>Ethiopian national curriculum · Grades 9–12 · exit exams</Eyebrow>
            <h1 className="mt-4 font-display text-[40px] font-extrabold leading-[1.02] tracking-[-0.04em] sm:text-[58px]">
              Practice that follows the syllabus. <span className="text-accent">Help before it’s too late.</span>
            </h1>
            <p className="mt-5 max-w-[540px] text-[17.5px] leading-relaxed text-on-navy">
              YADA is an AI study app for Ethiopian students and an early-warning panel for their schools.
              Students practise past and generated exams, even offline. Teachers and counsellors see
              who is falling behind this week, and why.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <PlayButton />
              <a href="#schools"
                className="inline-flex items-center gap-2 rounded-[14px] border border-white/25 px-5 py-[15px] font-semibold text-white no-underline hover:bg-white/10">
                <School size={18} strokeWidth={1.9} /> YADA for schools
              </a>
              <a href="#demo"
                className="inline-flex items-center gap-2 rounded-[14px] bg-accent px-5 py-[15px] font-semibold text-white no-underline hover:brightness-105">
                <CalendarCheck size={18} strokeWidth={1.9} /> Book a demo
              </a>
            </div>
          </div>
          <div id="watch" className="relative scroll-mt-24">
            <div aria-hidden className="absolute -inset-3 rounded-[26px] bg-gradient-to-br from-accent/40 via-slate/20 to-transparent blur-xl" />
            <div className="relative rounded-[22px] border border-white/15 bg-white/5 p-2 shadow-[0_40px_80px_-30px_rgba(5,15,25,.8)] backdrop-blur">
              <YouTubeVideo id={AD_VIDEO.id} title={AD_VIDEO.title} duration={AD_VIDEO.duration} poster={adPoster} large />
            </div>
            <div className="relative mt-4 flex flex-wrap items-center justify-between gap-3 px-1">
              <p className="m-0 flex items-center gap-2 text-sm text-on-navy">
                <PlayCircle size={16} className="text-accent" /> YADA for Schools in two and a half minutes
              </p>
              <Link to="/tutorials" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white no-underline hover:text-accent">
                Watch the tutorials <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section aria-label="YADA in numbers" className="border-b border-line bg-surface">
        <dl className="mx-auto grid max-w-[1200px] grid-cols-2 gap-6 px-4 py-8 sm:px-6 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.l}>
              <dt className="sr-only">{s.l}</dt>
              <dd className="m-0 font-mono text-[28px] font-semibold tracking-[-0.03em] text-ink">{s.v}</dd>
              <dd className="m-0 mt-1 text-sm text-muted">{s.l}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Students */}
      <section id="students" className="scroll-mt-20">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <Eyebrow>For students</Eyebrow>
            <h2 className="mt-3 font-display text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[42px]">
              A study partner that knows your textbook.
            </h2>
            <p className="mt-4 max-w-[560px] text-[16.5px] leading-relaxed text-muted">
              Everything is tagged to the Ethiopian curriculum by grade, subject and chapter, so practice
              goes where your marks are weakest, not where a generic app guesses.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <Feature icon={<WifiOff size={20} />} title="Practise offline">
                Download a subject once and take past and practice exams without using mobile data.
              </Feature>
              <Feature icon={<Bot size={20} />} title="AI tutor from your textbooks">
                Ask in English, Amharic or Afaan Oromoo. Answers come from the national textbooks, with step-by-step explanations.
              </Feature>
              <Feature icon={<Layers size={20} />} title="Notes and flashcards">
                Turn any chapter into revision notes and flashcards, and keep them for the night before the exam.
              </Feature>
              <Feature icon={<GraduationCap size={20} />} title="School mode">
                Worksheets, notices and results from your own teachers, with a weekly plan of what’s due.
              </Feature>
              <Feature icon={<Activity size={20} />} title="Progress you can read">
                Weekly trends and subject mastery against your own target score.
              </Feature>
              <Feature icon={<Briefcase size={20} />} title="Careers and scholarships">
                Departments and universities matched to your strengths, and a scholarship tracker with reminders.
              </Feature>
            </div>
          </div>
          <div className="mx-auto grid w-full max-w-[560px] grid-cols-3 items-start gap-3 sm:gap-5">
            <Phone src={studentProgress} alt="Student progress screen with weekly trend and subject mastery" className="mt-16" />
            <Phone src={studentToday} alt="Student Today screen" />
            <Phone src={studentTutor} alt="AI tutor screen offering to explain a concept, create flashcards or quiz" className="mt-24" />
          </div>
        </div>
      </section>

      {/* Schools */}
      <section id="schools" className="scroll-mt-20 border-y border-line bg-surface">
        <div className="mx-auto max-w-[1200px] px-4 py-20 sm:px-6">
          <div className="max-w-[720px]">
            <Eyebrow>For schools</Eyebrow>
            <h2 className="mt-3 font-display text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[42px]">
              Know who needs help this week, not after the national exam.
            </h2>
            <p className="mt-4 text-[16.5px] leading-relaxed text-muted">
              The YADA School panel turns students’ practice, exam results and check-ins into a clear
              picture for directors, teachers and counsellors, on the web and in the staff mobile app.
            </p>
          </div>

          <div className="mt-10">
            <div role="tablist" aria-label="School panel screens" className="inline-flex max-w-full gap-1 overflow-x-auto rounded-[14px] border border-line bg-surface-2 p-1">
              {SHOWCASE.map((s) => (
                <button key={s.key} type="button" role="tab" aria-selected={shot === s.key}
                  onClick={() => setShot(s.key)}
                  className={`shrink-0 whitespace-nowrap rounded-[10px] px-3 py-2 text-sm font-semibold transition sm:px-4 ${shot === s.key ? 'bg-navy text-white dark:bg-slate' : 'text-muted hover:text-ink'}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-4 max-w-[640px] text-[15px] text-muted">{active.caption}</p>
            <Browser key={active.key} src={active.src} alt={`YADA School panel: ${active.label}`} className="mt-6" />
          </div>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Feature icon={<BellRing size={20} />} title="Risk scores and alerts">
              A nightly risk score for every student, explained in points. Alerts for engagement drops,
              academic decline and wellbeing check-ins.
            </Feature>
            <Feature icon={<Users size={20} />} title="Cases and follow-up">
              Open a case from an alert, add notes, assign a counsellor and track remediation cycles until the student recovers.
            </Feature>
            <Feature icon={<Sparkles size={20} />} title="AI exam generation and import">
              Generate exams from your chapters, or import them from photos, PDFs and Word files. Nothing reaches students until you approve it.
            </Feature>
            <Feature icon={<FileScan size={20} />} title="Paper exams, auto-marked">
              Print answer sheets, scan them with the staff app’s camera, and results flow into every report.
            </Feature>
            <Feature icon={<PenLine size={20} />} title="Worksheets and written answers">
              Assign worksheets, collect typed or photographed answers, and mark written work in one queue.
            </Feature>
            <Feature icon={<Mail size={20} />} title="Weekly parent digest">
              Families get a short weekly update on progress and upcoming exams by email or Telegram.
            </Feature>
          </div>

          <div className="mt-16 grid items-center gap-10 rounded-[24px] bg-navy p-6 text-white sm:p-10 lg:grid-cols-[1fr_auto]">
            <div>
              <div className="flex items-center gap-2 text-accent"><Smartphone size={18} /><Eyebrow onNavy>Staff app</Eyebrow></div>
              <h3 className="mt-3 font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em]">
                The panel in every teacher’s pocket.
              </h3>
              <p className="mt-3 max-w-[520px] text-on-navy">
                Teachers and counsellors get push alerts, open Student 360 between classes, write exams,
                scan paper answer sheets and follow up on cases from their phones.
              </p>
              <a href="#demo"
                className="mt-6 inline-flex items-center gap-2 rounded-[12px] bg-accent px-5 py-3 font-semibold text-white no-underline hover:brightness-105">
                <CalendarCheck size={18} /> Book a school demo
              </a>
            </div>
            <div className="mx-auto grid w-full max-w-[400px] grid-cols-2 items-start gap-4">
              <Phone src={staffHome} alt="Staff app home with class average, pass rate and students needing attention" />
              <Phone src={staffS360} alt="Staff app Student 360 with risk summary and weekly average" className="mt-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Tutorials */}
      <section id="tutorials" aria-labelledby="tutorials-title" className="scroll-mt-20">
        <div className="mx-auto max-w-[1200px] px-4 py-20 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-[640px]">
              <Eyebrow>Tutorials</Eyebrow>
              <h2 id="tutorials-title" className="mt-3 font-display text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[42px]">
                See the panel at work before you sign up.
              </h2>
              <p className="mt-4 text-[16.5px] leading-relaxed text-muted">
                {SCHOOL_TUTORIAL_LIST.length} short narrated videos, about {totalMinutes(SCHOOL_TUTORIAL_LIST)} minutes in all, take your
                staff from the first sign-in to the AI coach.
              </p>
            </div>
            <Link to="/tutorials"
              className="inline-flex items-center gap-2 rounded-[14px] bg-navy px-5 py-3.5 font-semibold text-white no-underline hover:bg-navy-2 dark:bg-slate">
              <PlayCircle size={18} /> All {SCHOOL_TUTORIAL_LIST.length} tutorials
            </Link>
          </div>
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURED_TUTORIALS.map((v) => (
              <li key={v.id} className="list-none">
                <Link to={`/tutorials?v=${v.n}`} className="group block no-underline">
                  <div className="relative aspect-video overflow-hidden rounded-[14px] bg-navy-deep">
                    <img src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`} alt="" loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]" />
                    <span aria-hidden className="absolute inset-0 grid place-items-center bg-navy-deep/20 opacity-0 transition group-hover:opacity-100">
                      <span className="grid h-12 w-12 place-items-center rounded-full bg-accent text-white"><PlayCircle size={22} /></span>
                    </span>
                    <span className="absolute bottom-2 right-2 rounded-[7px] bg-navy-deep/85 px-1.5 py-0.5 font-mono text-[11.5px] font-semibold text-white">{v.duration}</span>
                  </div>
                  <div className="mt-3 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{v.stage} · Part {v.n}</div>
                  <h3 className="mt-1 font-display text-[16.5px] font-bold leading-snug text-ink group-hover:text-accent-ink">{v.title}</h3>
                </Link>
              </li>
            ))}
          </ul>
          <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary">
            <Youtube size={17} /> More on the YADA YouTube channel
          </a>
        </div>
      </section>

      <BookDemo />

      {/* Technology */}
      <section aria-labelledby="tech" className="mx-auto max-w-[1200px] px-4 py-20 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <Eyebrow>How it works</Eyebrow>
            <h2 id="tech" className="mt-3 font-display text-[32px] font-extrabold leading-[1.1] tracking-[-0.03em] text-ink">
              Built on Google Gemini, designed for Ethiopian classrooms.
            </h2>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2">
            {[
              { i: <Sparkles size={18} />, t: 'Gemini on Google Cloud', d: 'Notes, flashcards, questions, tutoring and exam import run on Gemini models through Vertex AI.' },
              { i: <BookOpenCheck size={18} />, t: 'Grounded in textbooks', d: 'Answers are retrieved from the national textbooks before the model writes, so they match what is taught.' },
              { i: <Languages size={18} />, t: 'Three languages', d: 'The student app works in English, Amharic and Afaan Oromoo.' },
              { i: <ClipboardCheck size={18} />, t: 'Teachers stay in charge', d: 'AI drafts are reviewed by staff, and schools get a fixed monthly AI allowance.' },
            ].map((x) => (
              <li key={x.t} className="list-none rounded-[16px] border border-line bg-surface p-5">
                <div className="flex items-center gap-2 font-display font-bold text-ink"><span className="text-slate-ink">{x.i}</span>{x.t}</div>
                <p className="mt-2 text-[14.5px] leading-relaxed text-muted">{x.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="scroll-mt-20 border-t border-line bg-surface">
        <div className="mx-auto max-w-[1200px] px-4 py-20 sm:px-6">
          <Eyebrow>Team</Eyebrow>
          <h2 className="mt-3 font-display text-[32px] font-extrabold tracking-[-0.03em] text-ink">
            Built in Addis Ababa by {SITE.company}.
          </h2>
          <p className="mt-3 max-w-[640px] text-[16px] text-muted">
            We are a small, independent team building learning tools for Ethiopian students and the
            schools that teach them.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {TEAM.map((m) => (
              <article key={m.name} className="flex gap-4 rounded-[18px] border border-line bg-ground p-5">
                <div aria-hidden className="grid h-16 w-16 shrink-0 place-items-center rounded-[16px] bg-navy font-display text-xl font-extrabold text-white">
                  {initials(m.name)}
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-ink">{m.name}</h3>
                  <div className="text-sm font-semibold text-accent-ink">{m.role}</div>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-muted">{m.bio}</p>
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold text-primary">
                      LinkedIn →
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="scroll-mt-20 bg-navy text-white">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-8 px-4 py-16 sm:px-6">
          <div>
            <h2 className="font-display text-[30px] font-extrabold tracking-[-0.02em]">Bring YADA to your school.</h2>
            <p className="mt-2 max-w-[520px] text-on-navy">
              <a href="#demo" className="font-semibold text-white">Book a demo</a> or write to{' '}
              <a href={mailto()} className="font-semibold text-white">{SITE.email}</a> for a pilot or partnership. {SITE.company}, {SITE.city}.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <PlayButton />
            <a href={mailto('YADA for our school')}
              className="inline-flex items-center gap-2 rounded-[14px] bg-accent px-5 py-[15px] font-semibold text-white no-underline hover:brightness-105">
              <Mail size={18} /> Contact us
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
